<template>
  <div class="exception-summary">
    <div class="summary-header">
      <div class="header-icon">
        <i class="pi pi-exclamation-triangle"></i>
      </div>
      <h3 class="summary-title">Audit Exceptions Detected</h3>
      <p class="summary-subtitle">The following exceptions were found during the audit:</p>
    </div>

    <div class="exceptions-content">
      <div v-if="missingAnimals.length > 0" class="exception-section missing-section">
        <h4 class="section-title">
          <i class="pi pi-times-circle"></i>
          Missing Expected Animals ({{ missingAnimals.length }})
        </h4>
        <div class="animal-list">
          <div v-for="animal in missingAnimals" :key="animal.id" class="animal-item missing-item">
            <div class="animal-barcode">{{ getAnimalDisplayToken(animal) }}</div>
            <div v-if="animal.color" class="animal-color">Color: {{ animal.color }}</div>
          </div>
        </div>
      </div>

      <div v-if="unexpectedAnimals.length > 0" class="exception-section unexpected-section">
        <h4 class="section-title">
          <i class="pi pi-info-circle"></i>
          Unexpected Animals Found ({{ unexpectedAnimals.length }})
        </h4>
        <div class="animal-list">
          <div
            v-for="animal in unexpectedAnimals"
            :key="animal.rapidTagBarcode"
            class="animal-item unexpected-item"
          >
            <div class="animal-barcode">{{ animal.rapidTagBarcode }}</div>
            <div class="animal-timestamp">Scanned at: {{ formatTime(animal.scannedAt) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="cageNumberMismatch" class="exception-section mismatch-section">
      <h4 class="section-title">
        <i class="pi pi-exclamation-circle"></i>
        Cage Number Mismatch
      </h4>
      <p class="mismatch-message">
        The cage number entered during verification did not match the expected cage number for the
        scanned animals.
      </p>
    </div>

    <div v-if="showSupervisorPrompt" class="supervisor-notification">
      <div class="notification-content">
        <h4 class="notification-title">Supervisor Notification Required</h4>
        <p class="notification-message">
          These exceptions require supervisor notification. Please contact a supervisor to review
          and approve the audit results.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ExpectedAnimal, ScannedAnimal } from '../../stores/audit.store';

interface Props {
  missingAnimals: ExpectedAnimal[];
  unexpectedAnimals: ScannedAnimal[];
  cageNumberMismatch?: boolean;
  showSupervisorPrompt?: boolean;
}

withDefaults(defineProps<Props>(), {
  cageNumberMismatch: false,
  showSupervisorPrompt: true,
});

function getAnimalDisplayToken(animal: ExpectedAnimal): string {
  return animal.rapidTagBarcode || animal.rfidTagId || animal.id;
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
</script>

<style scoped>
.exception-summary {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 0.75rem;
  border: 2px solid #fbbf24;
  height: 100%;
  overflow: hidden;
}

.summary-header {
  text-align: center;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #fbbf24;
  flex-shrink: 0;
}

.header-icon {
  width: 3rem;
  height: 3rem;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.75rem;
  font-size: 1.5rem;
  color: white;
}

.summary-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #92400e;
  margin: 0 0 0.5rem 0;
}

.summary-subtitle {
  font-size: 1rem;
  color: #78350f;
  margin: 0;
}

.exceptions-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.exception-section {
  padding: 0.75rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
}

.missing-section {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
}

.unexpected-section {
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
}

.mismatch-section {
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.animal-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.animal-item {
  padding: 0.75rem;
  border-radius: 0.375rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.missing-item {
  background-color: white;
  border: 1px solid #fecaca;
}

.unexpected-item {
  background-color: white;
  border: 1px solid #fbbf24;
}

.animal-barcode {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  font-family: monospace;
  letter-spacing: 0.1em;
}

.animal-color {
  font-size: 0.875rem;
  color: #6b7280;
}

.animal-timestamp {
  font-size: 0.875rem;
  color: #6b7280;
}

.mismatch-message {
  font-size: 0.9375rem;
  color: #78350f;
  margin: 0;
  line-height: 1.5;
}

.supervisor-notification {
  margin-top: 0.5rem;
  padding: 0.75rem;
  background-color: #fef3c7;
  border: 2px solid #fbbf24;
  border-radius: 0.5rem;
  flex-shrink: 0;
}

.notification-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.notification-title {
  font-size: 1rem;
  font-weight: 600;
  color: #92400e;
  margin: 0;
}

.notification-message {
  font-size: 0.9375rem;
  color: #78350f;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .exception-summary {
    padding: 1rem;
  }

  .summary-title {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1rem;
  }
}
</style>
